import { contains } from '.';
export const toKebabCase = str => str.replace(/[A-Z\u00C0-\u00D6\u00D8-\u00DE]/g, s => `-${s.toLowerCase()}`);
export const toCamelCase = str => str.replace(/([a-zA-Z0-9])[_.-](\w|$)/g, (_, p, x) => p + x.toUpperCase());
/**
 * 对于需要类型安全性的地方，可以用这个来保护下
 *
 * @param x - can be string or anything else
 * @returns - always string
 */
export function toString(x) {
  if (typeof x === 'string') return x;
  if (!x || typeof x !== 'object') return String(x);
  try {
    return JSON.stringify(x);
  } catch (e) {
    return Object.prototype.toString.call(x);
  }
}
/**
 * 检查一个字符串是否是完全整数构成（比如 `"7"` `"-3"`），也就是说可以安全的使用 `~~x` 获得整数。
 *
 * 性能比正则表达式 `/^\d+$/` 好 33%
 */
export const isIntegerString = x => String(~~x) === String(x);
/**
 * 字符串哈希，使用 cyrb53 算法
 *
 * @see https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
 */
export function stringHash(str) {
  const seed = 0;
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
/**
 * 简单的拼接下路径地址
 *
 * @example
 *     simpleJoinPath("data", "[1]")  => "data[1]"
 *     simpleJoinPath("data", "foo.bar", 3)  => "data.foo.bar[3]"
 */
export function simpleJoinPath(...parts) {
  let ans = '';
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;
    if (!ans) ans = part;else if (/^\d+$/.test(part)) ans += `[${part}]`;else if (/^[a-zA-Z_$]/.test(part)) ans += `.${part}`;else ans += part;
  }
  return ans;
}
/**
 * 简单拆分路径地址
 *
 * @example
 *      simpleSplitPath("data[1]") => ["data", "[1]"]
 *      simpleSplitPath("data.foobar.x") => ["data", "foobar.x"]
 *      simpleSplitPath("[3].yy.x") => ["[3]", "yy.x"]
 */
export function simpleSplitPath(path) {
  if (typeof path === 'number') return [`[${path}]`, ''];
  if (!path || typeof path !== 'string') return ['', ''];
  let mid = 0;
  let isInString = '';
  while (mid < path.length) {
    const ch = path.charAt(mid);
    if (isInString) {
      if (ch === '\\') mid += 1;
      // eat extra one char
      else if (ch === isInString) isInString = '';
    } else {
      if (mid > 1 && (ch === '[' || ch === '.')) break;else if (ch === '"' || ch === '\'') isInString = ch;
    }
    mid += 1;
  }
  const mid2 = path.charAt(mid) === '.' ? mid + 1 : mid;
  return [path.slice(0, mid), path.slice(mid2)];
}
/**
 * 输入一段牛鬼蛇神，输出一个能用的变量名字
 *
 * 可选传入一个 Record<string,any> 或者 string[]，可做冲突的检查。如果有重名，会末尾追加数字
 */
export function getVariableName(basicName, existingVariables) {
  // 处理奇怪的字符，只保留字母数字下划线中文
  let tmp = basicName.replace(/[^\w$\u3000\u3400-\u4DBF\u4E00-\u9FFF]+([\w$\u3000\u3400-\u4DBF\u4E00-\u9FFF]?)/g, (_, x) => x.toUpperCase());
  if (!tmp) tmp = 'foobar';
  tmp = tmp[0].toLowerCase() + tmp.substr(1);
  if (/^\d/.test(tmp)) tmp = `_${tmp}`;
  // 避免冲突
  if (existingVariables) {
    if (contains(existingVariables, tmp)) {
      let suffix = 2;
      while (contains(existingVariables, tmp + suffix)) suffix += 1;
      tmp = tmp + suffix;
    }
  }
  return tmp;
}
/**
 * 如果 s 是一个非空字符串，那么就前后加上括号并返回值
 *
 * @example
 *   addParentheses('')  => ''
 *   addParentheses(null)  => ''
 *   addParentheses('hello')  => ' (hello)'
 *
 * @param {any} s
 */
export function addParentheses(s, before = ' (', after = ')') {
  if (!s && s !== 0) return '';
  return before + s + after;
}
/**
 * @example
 *   getTitleWithParentheses('')  => ''
 *   getTitleWithParentheses('aaa')  => 'aaa'
 *   getTitleWithParentheses('aaa', 'aaa')  => 'aaa'        // id 和 desc 相同
 *   getTitleWithParentheses('aaa', '名字')  => '名字 (aaa)'
 *
 * @param {string} id
 * @param {string | null | undefined} desc
 */
export function getTitleWithParentheses(id, desc) {
  if (id == null) return desc || ''; // eslint-disable-line eqeqeq
  if (typeof id !== 'string') id = String(id); // eslint-disable-line no-param-reassign
  if (!desc || id === desc) return id;
  return `${desc} (${id})`;
}
export function concatString(iter, separator = '\n') {
  return Array.from(iter).join(separator);
}
//# sourceMappingURL=string.js.map
